import { Link } from 'react-router-dom';

const industries = [
  {
    icon: 'ri-tools-line',
    name: 'Home Services',
    tagline: 'Never miss a booked job again.',
    description: 'When a call is missed, the opportunity is lost. Our systems capture every inbound lead, qualify it, and schedule the job — without requiring additional staff.',
    result: 'More booked jobs, fewer missed opportunities, higher revenue per lead.',
    image: 'https://public.readdy.ai/ai/img_res/57eb881c564697fd2b9eddd34be17be7.jpg',
  },
  {
    icon: 'ri-briefcase-line',
    name: 'Professional Services',
    tagline: 'Structured intake. Predictable pipeline.',
    description: 'Law firms, consultants, and advisory businesses rely on timely intake. We implement systems that capture inquiries, qualify prospects, and ensure every lead is handled with speed.',
    result: 'Higher-quality clients, improved intake efficiency, stronger conversion rates.',
    image: 'https://readdy.ai/api/search-image?query=modern%20professional%20office%20desk%20with%20laptop%20and%20documents%20on%20clean%20white%20background%2C%20minimal%20business%20photography%20style%2C%20soft%20light%20gray%20tones%2C%20clean%20professional%20aesthetic%2C%20no%20people&width=420&height=300&seq=ind002&orientation=landscape',
  },
  {
    icon: 'ri-building-line',
    name: 'Real Estate',
    tagline: 'Speed-to-lead wins deals.',
    description: 'Leads lose value within minutes if not engaged. AlphaOps AI enables instant response to new inquiries, automated follow-up, and centralized tracking of every interaction.',
    result: 'Faster speed-to-lead, increased engagement, more closed deals.',
    image: 'https://readdy.ai/api/search-image?query=modern%20real%20estate%20property%20exterior%20architecture%20on%20clean%20white%20background%2C%20minimal%20architectural%20photography%20style%2C%20soft%20light%20gray%20tones%2C%20clean%20aesthetic%2C%20no%20people&width=420&height=300&seq=ind003&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    name: 'Healthcare',
    tagline: 'Optimized patient intake at scale.',
    description: 'Missed calls and manual coordination create inefficiencies and lost revenue. We deploy systems that automate patient communication and ensure appointments are consistently booked.',
    result: 'Improved patient experience, higher appointment volume, optimized front-desk operations.',
    image: 'https://readdy.ai/api/search-image?query=modern%20healthcare%20medical%20equipment%20and%20stethoscope%20on%20clean%20white%20background%2C%20minimal%20medical%20photography%20style%2C%20soft%20light%20gray%20tones%2C%20clean%20professional%20aesthetic%2C%20no%20people&width=420&height=300&seq=ind004&orientation=landscape',
  },
];

export default function IndustriesPreview() {
  return (
    <section id="industries" className="bg-[#0A0A0A] py-28 md:py-36">
      <div className="max-w-[1360px] mx-auto px-8 md:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <span className="inline-block border border-white/20 text-white text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
              Industries
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-white leading-tight max-w-[560px]">
              Built for{' '}
              <span className="font-semibold">Service-Based</span>{' '}
              Businesses
            </h2>
          </div>
          <Link
            to="/industries"
            className="flex items-center gap-2 text-[#38C6F5] text-sm hover:gap-4 transition-all duration-200 cursor-pointer whitespace-nowrap self-start md:self-end"
          >
            View All Industries
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Industry Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry) => (
            <div
              key={industry.name}
              className="bg-[#F5F5F0] rounded-2xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="w-full h-[180px] overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A] rounded-lg mb-4">
                  <i className={`${industry.icon} text-[#38C6F5] text-lg`}></i>
                </div>
                <h3 className="text-[#0A0A0A] text-lg font-semibold mb-1">{industry.name}</h3>
                <p className="text-[#555555] text-xs font-medium mb-3">{industry.tagline}</p>
                <p className="text-[#777777] text-sm leading-relaxed mb-4">{industry.description}</p>
                <div className="border-t border-[#0A0A0A]/10 pt-4">
                  <p className="text-[#0A0A0A] text-xs font-semibold uppercase tracking-widest mb-1">Result</p>
                  <p className="text-[#555555] text-xs leading-relaxed">{industry.result}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Unified Advantage */}
        <div className="mt-16 border border-white/10 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#38C6F5] text-xs uppercase tracking-widest mb-4">Unified Advantage</p>
              <h3 className="text-white text-2xl md:text-3xl font-light leading-tight">
                Across every industry, the outcome remains{' '}
                <span className="font-semibold">consistent.</span>
              </h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-timer-flash-line', label: 'Faster Response Times' },
                { icon: 'ri-user-follow-line', label: 'Improved Lead Handling' },
                { icon: 'ri-line-chart-line', label: 'Increased Conversion Rates' },
                { icon: 'ri-shield-check-line', label: 'Stronger Operational Control' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center text-[#38C6F5] flex-shrink-0">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <span className="text-[#A0A0A0] text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
